/**
 * LeadDetailDrawer — dettaglio/modifica di un'azienda del CRM di piattaforma.
 *
 * Tutti i campi sono editabili con updateDoc diretto (pattern console
 * super_admin). Mostra anche ciò che arriva dai questionari (referente,
 * provenienza, consenso, «non contattare») e le risposte, con dettaglio
 * leggibile domanda → risposta.
 *
 * La partita IVA può mancare: chi si iscrive da un questionario senza
 * partita IVA è riconosciuto dall'email (`email_normalizzata`).
 */

import React, { useEffect, useState } from 'react';
import {
  Alert, Autocomplete, Box,
  Button, CircularProgress, Divider, Drawer, FormControlLabel, IconButton,
  MenuItem, Stack, Switch, TextField, Typography,
} from '@mui/material';
import {
  Close as CloseIcon, OpenInNew, Save as SaveIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { tokens } from '../../theme';
import { SoftChip } from '../SoftChip';
import {
  findLeadByPartitaIva, listResponsesForLead, updatePlatformLead, validatePartitaIva,
} from '../../services/platformCrmService';
import type {
  PlatformLead, PlatformLeadFonte, PlatformLeadStato, PlatformQuestionnaire,
  PlatformQuestionnaireResponse,
} from '../../types/platformCrm';
import {
  FONTE_LABELS, FONTE_ORDER, STATO_LABELS, STATO_ORDER, STATO_TONES,
  formatDateTime,
} from './labels';
import { RispostaQuestionarioAccordion } from './RispostaQuestionarioAccordion';
import { SezioneQuestionarioLead } from './SezioneQuestionarioLead';
import { PercorsoLead } from './PercorsoLead';
import { ConversazioniDellIscritto } from '../yuma-sito/ConversazioniDellIscritto';

const DRAWER_WIDTH = 520;

interface FormState {
  ragione_sociale: string;
  partita_iva: string;
  email: string;
  telefono: string;
  citta: string;
  provincia: string;
  settore: string;
  fonte: PlatformLeadFonte;
  stato: PlatformLeadStato;
  partner_segnalatore: string;
  founding_partner: boolean;
  note: string;
}

function toFormState(lead: PlatformLead): FormState {
  return {
    ragione_sociale: lead.ragione_sociale ?? '',
    partita_iva: lead.partita_iva ?? '',
    email: lead.email ?? '',
    telefono: lead.telefono ?? '',
    citta: lead.citta ?? '',
    provincia: lead.provincia ?? '',
    settore: lead.settore ?? '',
    fonte: lead.fonte ?? 'manuale',
    stato: lead.stato ?? 'nuovo',
    partner_segnalatore: lead.partner_segnalatore ?? '',
    founding_partner: lead.founding_partner === true,
    note: lead.note ?? '',
  };
}

interface Props {
  /** Lead aperto nel drawer (null = drawer chiuso). */
  lead: PlatformLead | null;
  /** Definizioni questionari per risolvere domanda → testo nelle risposte. */
  questionnaires: Map<string, PlatformQuestionnaire>;
  /**
   * Partner segnalatori già presenti nei lead caricati: alimentano
   * l'autocomplete per coerenza dei nomi, senza hardcode.
   */
  partnerOptions: string[];
  onClose: () => void;
  /** Chiamata dopo un salvataggio riuscito (il padre ricarica la lista). */
  onSaved: () => void;
  showSnack: (msg: string, severity: 'success' | 'error') => void;
}

export const LeadDetailDrawer: React.FC<Props> = ({
  lead, questionnaires, partnerOptions, onClose, onSaved, showSnack,
}) => {
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [responses, setResponses] = useState<PlatformQuestionnaireResponse[]>([]);
  const [responsesLoading, setResponsesLoading] = useState(false);

  // Reset form + caricamento risposte al cambio di lead
  useEffect(() => {
    if (!lead) {
      setForm(null);
      setResponses([]);
      return;
    }
    setForm(toFormState(lead));
    setDirty(false);
    setError(null);
    let cancelled = false;
    setResponsesLoading(true);
    listResponsesForLead(lead.id)
      .then((r) => { if (!cancelled) setResponses(r); })
      .catch((e) => {
        console.error('[platform-crm] caricamento risposte lead fallito', e);
        if (!cancelled) setResponses([]);
      })
      .finally(() => { if (!cancelled) setResponsesLoading(false); });
    return () => { cancelled = true; };
  }, [lead]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((s) => (s ? { ...s, [key]: value } : s));
    setDirty(true);
    setError(null);
  };

  const handleSave = async () => {
    if (!lead || !form) return;
    const ragioneSociale = form.ragione_sociale.trim();
    if (!ragioneSociale) {
      setError('La ragione sociale è obbligatoria');
      return;
    }
    // La partita IVA può mancare se c'è l'email (chi si iscrive da un questionario senza
    // partita IVA è riconosciuto dall'email); se c'è, dev'essere valida.
    const pivaGrezza = form.partita_iva.trim();
    let pivaNormalizzata = '';
    if (pivaGrezza) {
      const piva = validatePartitaIva(pivaGrezza);
      if (!piva.ok) {
        setError(`Partita IVA: ${piva.error}`);
        return;
      }
      pivaNormalizzata = piva.normalized;
    } else if (!form.email.trim()) {
      setError("Serve la partita IVA o l'email: una delle due riconosce l'azienda");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      // Dedupe se la P.IVA è cambiata (esclude il record stesso)
      if (pivaNormalizzata && pivaNormalizzata !== (lead.partita_iva ?? '')) {
        const existing = await findLeadByPartitaIva(pivaNormalizzata);
        if (existing && existing.id !== lead.id) {
          setError(`P.IVA già presente su "${existing.ragione_sociale}"`);
          return;
        }
      }
      await updatePlatformLead(lead.id, {
        ragione_sociale: ragioneSociale,
        partita_iva: pivaNormalizzata,
        email: form.email.trim(),
        telefono: form.telefono.trim(),
        citta: form.citta.trim(),
        provincia: form.provincia.trim().toUpperCase(),
        settore: form.settore.trim(),
        fonte: form.fonte,
        stato: form.stato,
        partner_segnalatore: form.partner_segnalatore.trim(),
        founding_partner: form.founding_partner,
        note: form.note.trim(),
      });
      setDirty(false);
      showSnack('Azienda aggiornata', 'success');
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore durante il salvataggio');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={!!lead}
      onClose={onClose}
      // Sopra l'AppBar della console (che sta a drawer+1 per la sidebar):
      // senza questo la testata del pannello finisce nascosta sotto la barra.
      sx={{ zIndex: (t) => t.zIndex.modal }}
      PaperProps={{ sx: { width: { xs: '100%', sm: DRAWER_WIDTH } } }}
    >
      {lead && form && (
        <Box sx={{ p: 3 }}>
          {/* Header */}
          <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ mb: 1 }}>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                sx={{
                  fontFamily: tokens.font.display,
                  fontWeight: 600,
                  fontSize: '1.4rem',
                  lineHeight: 1.15,
                  letterSpacing: '-0.02em',
                  color: tokens.color.graphite,
                  overflowWrap: 'anywhere',
                }}
              >
                {lead.ragione_sociale}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 0.75 }}>
                {lead.partita_iva
                  ? <SoftChip label={lead.partita_iva} tone="default" mono />
                  : lead.email && <SoftChip label={lead.email} tone="default" mono />}
                {lead.non_contattare && <SoftChip label="Non contattare" tone="error" />}
                <SoftChip label={STATO_LABELS[form.stato]} tone={STATO_TONES[form.stato]} />
                <SoftChip label={FONTE_LABELS[form.fonte]} tone="info" />
                {form.founding_partner && <SoftChip label="Founding" tone="gold" />}
              </Stack>
            </Box>
            <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
          </Stack>

          {lead.converted_tenant_id && (
            <Alert
              severity="success"
              sx={{ mb: 2 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  startIcon={<OpenInNew fontSize="small" />}
                  onClick={() => navigate(`/tenants/${lead.converted_tenant_id}`)}
                >
                  Apri tenant
                </Button>
              }
            >
              Azienda convertita in tenant.
            </Alert>
          )}

          {/* Percorso: a che punto è e cosa abbiamo fatto (registro delle azioni, 20/10/2026) */}
          <Box sx={{ mt: 2.5 }}>
            <PercorsoLead lead={lead} />
            {/* La conversazione con Yuma sul sito da cui è nata l'iscrizione, se c'è (30/10/2026) */}
            <ConversazioniDellIscritto leadId={lead.id} />
          </Box>
          <Divider sx={{ my: 3 }} />

          {/* Form campi */}
          <Stack spacing={2} sx={{ mt: 1.5 }}>
            <Stack direction="row" spacing={2}>
              <TextField
                select label="Stato funnel" value={form.stato}
                onChange={(e) => set('stato', e.target.value as PlatformLeadStato)}
                fullWidth
              >
                {STATO_ORDER.map((s) => (
                  <MenuItem key={s} value={s}>{STATO_LABELS[s]}</MenuItem>
                ))}
              </TextField>
              <TextField
                select label="Fonte" value={form.fonte}
                onChange={(e) => set('fonte', e.target.value as PlatformLeadFonte)}
                fullWidth
              >
                {FONTE_ORDER.map((f) => (
                  <MenuItem key={f} value={f}>{FONTE_LABELS[f]}</MenuItem>
                ))}
              </TextField>
            </Stack>
            <TextField
              label="Ragione sociale"
              value={form.ragione_sociale}
              onChange={(e) => set('ragione_sociale', e.target.value)}
              fullWidth
            />
            <TextField
              label="Partita IVA"
              value={form.partita_iva}
              onChange={(e) => set('partita_iva', e.target.value)}
              fullWidth
              helperText={form.partita_iva.trim() ? undefined : "Facoltativa se c'è l'email"}
              InputProps={{ sx: { fontFamily: tokens.font.mono } }}
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="Email" type="email" value={form.email}
                onChange={(e) => set('email', e.target.value)} fullWidth
              />
              <TextField
                label="Telefono" value={form.telefono}
                onChange={(e) => set('telefono', e.target.value)} fullWidth
              />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Città" value={form.citta}
                onChange={(e) => set('citta', e.target.value)} fullWidth
              />
              <TextField
                label="Provincia" value={form.provincia}
                onChange={(e) => set('provincia', e.target.value)}
                sx={{ width: 140 }}
                inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
              />
            </Stack>
            <TextField
              label="Settore" value={form.settore}
              onChange={(e) => set('settore', e.target.value)} fullWidth
            />
            <Stack direction="row" spacing={2} alignItems="center">
              <Autocomplete
                freeSolo
                options={partnerOptions}
                inputValue={form.partner_segnalatore}
                onInputChange={(_, value) => set('partner_segnalatore', value)}
                sx={{ flex: 1 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Partner segnalatore"
                    placeholder="es. Forniture Fotovoltaiche"
                    inputProps={{ ...params.inputProps, maxLength: 80 }}
                  />
                )}
              />
              <FormControlLabel
                control={(
                  <Switch
                    checked={form.founding_partner}
                    onChange={(e) => set('founding_partner', e.target.checked)}
                  />
                )}
                label={<Typography variant="body2">Founding partner</Typography>}
                sx={{ flexShrink: 0, mr: 0 }}
              />
            </Stack>
            <TextField
              label="Note" value={form.note}
              onChange={(e) => set('note', e.target.value)}
              fullWidth multiline minRows={3}
            />
          </Stack>

          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!dirty || saving}
              startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
            >
              {saving ? 'Salvataggio...' : 'Salva modifiche'}
            </Button>
          </Stack>

          {/* Metadati */}
          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
            <Typography variant="caption" sx={{ color: tokens.color.mist }}>
              Creato: {formatDateTime(lead.created_at)}
            </Typography>
            <Typography variant="caption" sx={{ color: tokens.color.mist }}>
              Aggiornato: {formatDateTime(lead.updated_at)}
            </Typography>
          </Stack>

          <Divider sx={{ my: 3 }} />

          <SezioneQuestionarioLead
            lead={lead}
            lingua={responses.find((r) => r.lingua)?.lingua ?? null}
            onCambiato={onSaved}
            showSnack={showSnack}
          />

          <Divider sx={{ my: 3 }} />

          {/* Risposte questionari */}
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
            Risposte questionari
          </Typography>
          {responsesLoading ? (
            <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress size={22} /></Box>
          ) : responses.length === 0 ? (
            <Typography variant="body2" sx={{ color: tokens.color.mist }}>
              Nessuna risposta registrata per questa azienda.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {responses.map((r) => (
                <RispostaQuestionarioAccordion
                  key={r.id}
                  response={r}
                  questionnaire={questionnaires.get(r.questionnaire_id)}
                />
              ))}
            </Stack>
          )}
        </Box>
      )}
    </Drawer>
  );
};
